/**
 * Block Style System — Single source of truth
 * Defines style properties, feature tiers, block mappings, and shared class maps.
 */

// ─── Style defaults (all null = "use theme") ────────────────────────────────

export const styleDefaults = {
  // Heading tier
  headingSize: null,
  headingAlignment: null,
  headingFont: null,
  headingColor: null,
  // Body tier
  bodyFont: null,
  bodyColor: null,
  // Colors tier
  sectionBackground: null,
  cardBackground: null,
  accentColor: null,
  inputBackground: null,
  // Button tier
  buttonStyle: null,
  buttonColor: null,
  buttonTextColor: null,
  // Spacing tier
  sectionPadding: null,
};

// ─── Feature tiers ──────────────────────────────────────────────────────────

export const FEATURE_TIERS = ['heading', 'body', 'colors', 'button', 'spacing'];

export const TIER_PROPERTIES = {
  heading: ['headingSize', 'headingAlignment', 'headingFont', 'headingColor'],
  body: ['bodyFont', 'bodyColor'],
  colors: ['sectionBackground', 'cardBackground', 'accentColor', 'inputBackground'],
  button: ['buttonStyle', 'buttonColor', 'buttonTextColor'],
  spacing: ['sectionPadding'],
};

// ─── Block → feature tier mapping ───────────────────────────────────────────

export const BLOCK_FEATURES = {
  'rich-text':        ['heading', 'body', 'colors', 'spacing'],
  'button-cta':       ['heading', 'body', 'colors', 'button', 'spacing'],
  calendly:           ['heading', 'body', 'colors', 'button', 'spacing'],
  'cal-com':          ['heading', 'body', 'colors', 'button', 'spacing'],
  'calendar-embed':   ['heading', 'colors', 'spacing'],
  banner:             ['heading', 'body', 'colors', 'button', 'spacing'],
  faq:                ['heading', 'colors', 'spacing'],
  testimonials:       ['heading', 'colors', 'spacing'],
  'card-grid':        ['heading', 'colors', 'spacing'],
  'youtube-gallery':  ['heading', 'colors', 'spacing'],
  'social-links':     ['heading', 'colors', 'spacing'],
  blog:               ['heading', 'colors', 'spacing'],
  shop:               ['heading', 'colors', 'spacing'],
  'chess-game':       ['heading', 'colors', 'spacing'],
  'daily-puzzle':     ['heading', 'colors', 'spacing'],
  'favourite-games':  ['heading', 'colors', 'spacing'],
  'text-image':       ['heading', 'body', 'colors', 'button', 'spacing'],
  donations:          ['heading', 'body', 'colors', 'button', 'spacing'],
  referrals:          ['heading', 'body', 'colors', 'button', 'spacing'],
  contact:            ['heading', 'body', 'colors', 'button', 'spacing'],
  'email-subscribe':  ['heading', 'body', 'colors', 'button', 'spacing'],
  coaching:           ['heading', 'body', 'colors', 'button', 'spacing'],
  'content-library':  ['heading', 'body', 'colors', 'button', 'spacing'],
  'library-showcase': ['heading', 'body', 'colors', 'button', 'spacing'],
  'single-product':   ['heading', 'body', 'colors', 'button', 'spacing'],
  'bulletin-board':   ['heading', 'body', 'colors', 'spacing'],
  'coach-updates':    ['heading', 'body', 'colors', 'spacing'],
  'featured-blog':    ['body', 'colors', 'button', 'spacing'],
  downloads:          ['heading', 'body', 'colors', 'button', 'spacing'],
  'media-gallery':    ['heading', 'colors', 'spacing'],
  'file-embed':       ['heading', 'colors', 'spacing'],
  'sliding-text':     ['colors'],
  'video-embed':      ['colors'],
  divider:            ['colors'],
  'initiative-timeline': ['heading', 'colors', 'spacing'],
  'endorsement-wall': ['heading', 'colors', 'spacing'],
};

// ─── Panel → tier split (documents which controls go into which panel) ──────

export const PANEL_TIER_SPLIT = {
  sizeSpacing: {
    heading: ['headingSize', 'headingAlignment'],
    spacing: ['sectionPadding'],
  },
  colorsFonts: {
    heading: ['headingFont', 'headingColor'],
    body: ['bodyFont', 'bodyColor'],
    colors: ['sectionBackground', 'cardBackground', 'accentColor', 'inputBackground'],
    button: ['buttonStyle', 'buttonColor', 'buttonTextColor'],
  },
};

// ─── UI enum options ────────────────────────────────────────────────────────

export const HEADING_SIZE_OPTIONS = [
  { value: 'small', label: 'S' },
  { value: 'medium', label: 'M' },
  { value: 'large', label: 'L' },
  { value: 'xlarge', label: 'XL' },
];

export const ALIGNMENT_OPTIONS = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

export const BUTTON_STYLE_OPTIONS = [
  { value: 'primary', label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'outline', label: 'Outline' },
];

export const PADDING_OPTIONS = [
  { value: 'compact', label: 'Compact' },
  { value: 'normal', label: 'Normal' },
  { value: 'spacious', label: 'Spacious' },
];

// ─── Shared option arrays (for block-specific extraControls) ────────────────

export const MAX_WIDTH_OPTIONS = [
  { value: 'narrow', label: 'Narrow' },
  { value: 'normal', label: 'Normal' },
  { value: 'wide', label: 'Wide' },
];

export const IMAGE_SIZE_OPTIONS = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
  { value: 'auto', label: 'Auto' },
];

export const ICON_SIZE_OPTIONS = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
];

export const SPACING_AMOUNT_OPTIONS = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
];

// ─── Shared Tailwind class maps ─────────────────────────────────────────────

export const HEADING_SIZE_CLASSES = {
  small: 'text-lg sm:text-xl md:text-2xl',
  medium: 'text-xl sm:text-2xl md:text-3xl',
  large: 'text-2xl sm:text-3xl md:text-4xl',
  xlarge: 'text-3xl sm:text-4xl md:text-5xl lg:text-6xl',
};

export const SECTION_PADDING_CLASSES = {
  compact: 'px-4 sm:px-6 py-4 sm:py-6 md:py-8',
  normal: 'px-4 sm:px-6 py-8 sm:py-10 md:py-12 lg:py-16',
  spacious: 'px-4 sm:px-6 py-12 sm:py-16 md:py-20 lg:py-24',
};

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Returns a null-filled style object containing only the properties
 * relevant to the given block type's feature tiers.
 */
export const getDefaultStyle = (blockType) => {
  const tiers = BLOCK_FEATURES[blockType];
  if (!tiers) return {};

  const style = {};
  tiers.forEach((tier) => {
    (TIER_PROPERTIES[tier] || []).forEach((prop) => {
      style[prop] = null;
    });
  });
  return style;
};

/**
 * Scan all blocks for per-block headingFont / bodyFont overrides.
 * Returns a Set of font family strings that need loading via Google Fonts.
 */
export const getBlockFontOverrides = (blocks = []) => {
  const fonts = new Set();
  blocks.forEach((block) => {
    const style = block.content?.style;
    if (!style) return;
    if (style.headingFont) fonts.add(style.headingFont);
    if (style.bodyFont) fonts.add(style.bodyFont);
  });
  // Remove system/default fonts that don't need loading
  fonts.delete('inherit');
  fonts.delete('system-ui');
  return fonts;
};
